import { useEffect, useState } from "react";
import * as L from "leaflet";
import { useWindyMapStore } from "@/stores/windy-map-store";
import Slider from "rc-slider";
import { useParams } from "react-router-dom";
import useWindQuery from "@/query/wind-query";
import ReactDOMServer from "react-dom/server";
import WindPopup from "@/components/wind-popup";
import geojson from "@/geojson";
import { Icon } from "@/components/icon";
import useTempQuery from "@/query/temp-query";
import { getSeason } from "@/utils/get-season";

type GeojsonKey = keyof typeof geojson;

const types = {
  all: {
    zoom: 6,
    location: [46.11133, 100.96436],
    regions: [
      "алтай",
      "арвайхээр",
      "баруунурт",
      "баянхонгор",
      "булган",
      "даланзадгад",
      "зуунмод",
      "мандалговь",
      "мөрөн",
      "өлгий",
      "сайншанд",
      "сүхбаатар",
      "тосонцэнгэл",
      "улаангом",
      "улиастай",
      "ховд",
      "цэцэрлэг",
      "чингис",
      "чойбалсан",
      "чойр",
      "хатгал",
      "эрдэнэт",
      "дархан",
      "улаанбаатар",
    ],
  },
  western: {
    zoom: 7,
    location: [46.9967745294364, 93.72522509496832],
    regions: ["алтай", "баянхонгор", "өлгий", "улиастай", "ховд", "улаангом"],
  },
  central: {
    zoom: 7,
    location: [48.011706569490016, 102.2958779355153],
    regions: [
      "булган",
      "зуунмод",
      "мөрөн",
      "сүхбаатар",
      "цэцэрлэг",
      "арвайхээр",
      "хатгал",
    ],
  },
  eastern: {
    zoom: 7,
    location: [47.506069781910846, 113.13720703125001],
    regions: ["баруунурт", "чингис", "чойбалсан"],
  },
  gobi: {
    zoom: 7,
    location: [44.28847, 106.67725],
    regions: [
      "даланзадгад",
      "мандалговь",
      "сайншанд",
      "чойр",
      "замынүүд",
      "ханбогд",
    ],
  },
};

function hasLatLngString(s: unknown): s is string {
  if (typeof s !== "string") return false;
  const m = s.trim().match(/^\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*$/);
  if (!m) return false;
  const lat = parseFloat(m[1]);
  const lng = parseFloat(m[2]);
  return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
}

function parseLatLng(s: string): { lat: number; lng: number } | null {
  if (!hasLatLngString(s)) return null;
  const [latStr, lngStr] = s.split(",");
  const lat = parseFloat(latStr);
  const lng = parseFloat(lngStr);
  return { lat, lng };
}
const WindPage: React.FC = () => {
  // const [locationId, setLocationId] = useState<string>("1");
  // const [searchParams, setSearchParams] = useSearchParams();
  const { type } = useParams<{
    type: "all" | "central" | "western" | "eastern" | "gobi";
  }>();

  if (!type || !types[type]) {
    return <></>; // Handle invalid type
  }
  const { location, zoom } = types[type];
  const geojsonKeys = types[type].regions as GeojsonKey[];
  // const typeId = searchParams.get("type_id") ?? "1";

  const [isWind, setIsWind] = useState<boolean>(true);
  const [wind, setWind] = useState<number[]>([10, 20]);

  const initialIsSummer = getSeason(); // Call the function
  const initialTemp = initialIsSummer ? [30, 45] : [-30, -45];

  const [isSummer, setIsSummer] = useState<boolean>(initialIsSummer);
  const [temp, setTemp] = useState<number[]>(initialTemp);
  const layer = useWindyMapStore((state) => state.layer);
  const windyMap = useWindyMapStore((state) => state.windyMap);
  const mapStore = useWindyMapStore((state) => state.mapStore);
  const setOverlay = useWindyMapStore((state) => state.setOverlay);
  const [windLayer, setWindLayer] = useState<any>();
  const [geoLayer, setGeoLayer] = useState<any>();
  // Always call hooks at top level
  const { data: windData } = useWindQuery(wind, type ?? "all");
  const { data: tempData } = useTempQuery(temp, type ?? "all");

  // Choose which data to show
  const mapData = isWind ? windData : tempData;

  useEffect(() => {
    if (!layer) return;

    const newWindLayer = L.layerGroup().addTo(layer);
    const newGeoLayer = L.layerGroup().addTo(layer);

    // хуучин geoLayer байвал устгана
    if (geoLayer) {
      geoLayer.remove();
    }
    if (windLayer) {
      windLayer.remove();
    }

    setWindLayer(newWindLayer);
    setGeoLayer(newGeoLayer);
    return () => {
      newWindLayer.remove();
      newGeoLayer.remove();
    };
  }, [layer]);
  useEffect(() => {
    setIsSummer(getSeason());
  }, [isSummer]);
  useEffect(() => {
    if (windLayer) {
      windLayer.clearLayers();
    }
    if (mapData && mapData.length > 0 && windLayer) {
      mapData.forEach((item) => {
        if (hasLatLngString(item.label_location)) {
          const { lat, lng } = parseLatLng(item.label_location)!;
          const content = ReactDOMServer.renderToString(
            <WindPopup
              isWind={isWind}
              wind={isWind ? item.wind_day : item.temp_day}
              city={item.city}
            />
          );
          L.marker([lat, lng])
            .addTo(windLayer)
            .bindPopup(content, {
              closeButton: false,
              autoClose: false,
              closeOnEscapeKey: false,
              closeOnClick: false,
              className: "my-popup",
            })
            .openPopup();
        }
      });
    }
  }, [mapData, windLayer, geoLayer, isWind]);

  const tempMarks: Record<number, string> = isSummer
    ? {
        15: "+15",
        30: "+30",
        45: "+45",
      }
    : {
        [-45]: "-45",
        [-30]: "-30",
        [-15]: "-15",
      };

  useEffect(() => {
    if (!geoLayer || !windyMap) return;

    geoLayer.clearLayers();

    geojsonKeys.map((name) => {
      const geoData = geojson[name];
      L.geoJSON(geoData, {
        style: {
          color: "#4D0368",
        },
      }).addTo(geoLayer);
    });
    windyMap.setView(location, zoom);
  }, [geoLayer, geojsonKeys, windyMap]);

  return (
    <div className="absolute bottom-[80px] left-[80px] rounded-[20px] w-[300px] bg-gradient-to-b from-[#AEC9FF] to-[#083072] text-[20px] font-bold text-white p-6 z-10">
      <div className="flex justify-between items-center mb-2">
        {/* Wind Button */}
        <button
          onClick={() => {
            setIsWind(true);
            setOverlay("wind");
            mapStore.set("overlay", "wind");
          }}
          className={`flex items-center gap-1 rounded-md px-2 py-1 text-sm font-medium transition-colors ${
            isWind
              ? "text-white border border-white/40"
              : "text-white border border-transparent hover:border-white/20"
          }`}
        >
          <Icon name="wind.svg" alt="wind" className="h-3 w-3" />
          <span>салхины хурд</span>
        </button>

        {/* Temperature Button */}
        <button
          onClick={() => {
            setIsWind(false);
            setOverlay("temp");
            mapStore.set("overlay", "temp");
          }}
          className={`flex items-center gap-1 rounded-md px-2 py-1 text-sm font-medium transition-colors ${
            !isWind
              ? "text-white border border-white/40"
              : "text-white border border-transparent hover:border-white/20"
          }`}
        >
          <Icon name="temp.svg" alt="temp" className="h-3 w-3" />
          <span>температур</span>
        </button>
      </div>

      <div className="mb-2 flex gap-2 items-center">
        <span>{isWind ? "Салхины хурд:" : "Температур:"}</span>
        <span className="text-lg font-normal flex items-center justify-center">
          {isWind
            ? `${wind[0]} - ${wind[1]} м/с`
            : `${temp[0]} - ${temp[1]} °C`}
        </span>
      </div>
      <div className="h-10">
        {isWind ? (
          <Slider
            key={"wind-slider"}
            defaultValue={[0, 10]}
            onChangeComplete={(v: any) => {
              const p = [20 - v[1], 20 - v[0]];
              setWind(p);
            }}
            reverse
            range
            step={2}
            max={20}
            marks={{
              20: "0",
              10: "+10",
              0: "+20",
            }}
          />
        ) : (
          <Slider
            key={"temp-slider"}
            defaultValue={isSummer ? [30, 45] : [-30, -45]}
            onChangeComplete={(v: any) => {
              console.log("onchange", v);
              if (isSummer) {
                setTemp(v);
              } else {
                setTemp([v[1], v[0]]);
              }
            }}
            reverse={!isSummer}
            range
            step={2}
            min={isSummer ? 15 : -45}
            max={isSummer ? 45 : -15}
            marks={tempMarks}
          />
        )}
      </div>
    </div>
  );
};
export default WindPage;
